"use client";

import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import {
  thumb,
  yt,
  isMobile,
  videoMeta,
  claimEmbed,
  releaseEmbed,
  registerEmbed,
} from "@/lib/youtube";
import { cn } from "@/lib/utils";

/**
 * VideoCard — Netflix/YouTube-style card that shows a thumbnail by default
 * and only loads a YouTube iframe on explicit click.
 *
 * Rules (per brief):
 *   - default state = thumbnail only, no iframe
 *   - mobile: opens YouTube link in new tab (no inline iframe)
 *   - desktop: click swaps thumbnail for iframe
 *   - only ONE iframe active at a time (enforced via global activeEmbedId)
 *   - thumbnail falls back to hqdefault on error
 */
export default function VideoCard({ video, vertical = false, className = "" }) {
  const [active, setActive] = useState(false);
  const [imgSrc, setImgSrc] = useState(
    video.thumbnailHigh || thumb(video.id, "maxresdefault")
  );
  const triedFallback = useRef(false);

  useEffect(() => {
    if (!active) return;
    const stop = () => setActive(false);
    const unregister = registerEmbed(video.id, stop);
    return () => {
      unregister();
      releaseEmbed(video.id);
    };
  }, [active, video.id]);

  const onClick = (e) => {
    if (isMobile()) {
      // let the anchor's default behavior open YouTube in a new tab
      return;
    }
    e.preventDefault();
    claimEmbed(video.id);
    setActive(true);
  };

  const href = yt(video.id, !!video.isShort);

  const aspect = vertical ? "aspect-[9/16]" : "aspect-video";

  return (
    <div
      className={cn(
        "group relative shrink-0",
        vertical ? "w-[200px] md:w-[240px]" : "w-[300px] md:w-[360px]",
        className
      )}
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className={cn(
          "relative block overflow-hidden rounded-xl border border-black/5 bg-black",
          aspect
        )}
      >
        {!active ? (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={imgSrc}
              alt={video.title || "Video thumbnail"}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              onError={() => {
                if (!triedFallback.current) {
                  triedFallback.current = true;
                  setImgSrc(thumb(video.id, "hqdefault"));
                }
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-black backdrop-blur transition-transform duration-300 group-hover:scale-110">
                <Play className="ml-0.5 h-6 w-6 fill-black" />
              </div>
            </div>
          </>
        ) : (
          <iframe
            src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1`}
            title={video.title || "YouTube video"}
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        )}
      </a>

      {(video.title || videoMeta(video)) && (
        <div className="mt-3">
          {video.title && (
            <div className="line-clamp-2 text-sm leading-snug">{video.title}</div>
          )}
          {videoMeta(video) && (
            <div className="mt-1 text-xs text-muted-foreground">{videoMeta(video)}</div>
          )}
        </div>
      )}
    </div>
  );
}
